import type { ErrorMessage } from '@/types/base'
import { defineStore } from 'pinia'
import { Ref, ref } from 'vue'
import { reportErrorWithDetails } from '@/api/errorReportingApi'

export interface ErrorDetails {
    show: boolean
    message: string
    details?: string
    reported?: boolean
    reportedAt?: Date
    redirectTo?: string  // Optional: route name to redirect to after error is handled (default: 'events-search')
}

export interface UseErrorStoreReturn {
    error: Ref<ErrorDetails>
    sendError: (newError: ErrorMessage | ErrorDetails) => void
    sendErrorWithDetails: (message: string, details: string, redirectTo?: string) => void
    clearError: () => void
    reportError: (context?: {
        eventId?: number
        eventTitle?: string
        organizerId?: string
        organizerName?: string
        source?: string
        statusCode?: number
        userAdditionalInfo?: string
    }) => Promise<void>
}
export const useErrorStore = defineStore('errorStore', (): UseErrorStoreReturn => {
    const error = ref<ErrorDetails>({ show: false, message: '' })
    
    function sendError(newError: ErrorMessage | ErrorDetails) {
        if (!error?.value) return
        error.value.show = true
        error.value.message = newError.message
        error.value.details = (newError as ErrorDetails)?.details || undefined
        error.value.reported = false
    }
    
    function sendErrorWithDetails(message: string, details: string, redirectTo?: string) {
        if (!error?.value) return
        error.value.show = true
        error.value.message = message
        error.value.details = details
        error.value.reported = false
        error.value.redirectTo = redirectTo || 'events-search'  // Default to 'events-search'
    }
    
    function clearError() {
        if (!error?.value) return
        error.value.show = false
        error.value.details = undefined
        error.value.reported = false
        error.value.redirectTo = undefined
    }
    
    async function reportError(context?: {
        eventId?: number
        eventTitle?: string
        organizerId?: string
        organizerName?: string
        source?: string
        statusCode?: number
        userAdditionalInfo?: string
    }) {
        try {
            if (!error?.value?.message && !error?.value?.details) {
                console.warn('[errorStore] No error to report')
                return
            }

            // Try to extract context from stored details if not provided
            let enrichedContext = { ...context }
            
            if (error.value.details && !context?.eventId) {
                try {
                    const parsedDetails = JSON.parse(error.value.details)
                    enrichedContext = {
                        eventId: parsedDetails.eventId || context?.eventId,
                        statusCode: parsedDetails.statusCode || context?.statusCode,
                        source: parsedDetails.source || context?.source,
                        ...context
                    }
                } catch (parseError) {
                    // Couldn't parse details - that's okay, just use provided context
                }
            }

            // Report the error to support team
            await reportErrorWithDetails(
                error.value.message || 'Unknown error',
                error.value.details || 'No technical details available',
                enrichedContext
            )

            // Mark as reported
            if (error?.value) {
                error.value.reported = true
                error.value.reportedAt = new Date()
            }
        } catch (err) {
            console.error('[errorStore] Failed to report error:', err)
            // Don't re-throw - allow error reporting to fail silently
        }
    }
    
    return { error, sendError, sendErrorWithDetails, clearError, reportError }
})

